import { Box, CardBody } from '@chakra-ui/react';
import type { SortingState } from '@tanstack/react-table';
import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { PostingDiscoveryRow } from '@revelio/data-access';
import { useChartTheme } from '@revelio/replots';
import { Tag } from '@revelio/ui';

import { GlobalAiChartPreviewDataReporter } from '../../../../global-ai-assistant/global-ai-assistant-chart-preview-data-reporter';
import { useFilterContext } from '../../../filters/contexts/FilterProvider';
import { FilterId, type PrimaryFilterId } from '../../../filters/types/filters';
import { dimensionSourceToFilterId } from '../../../utils/dimension-source-serialization';
import { isScreenerV2Page } from '../../../utils/routing-helpers';
import type { MultiMetricProviderResult } from '../../DataProvider/MultiMetricProvider';
import { PostingDiscoveryDataProvider } from '../../DataProvider/PostingDiscoveryDataProvider';
import { ScreenerTableMultiMetricProvider } from '../../DataProvider/ScreenerTableMultiMetricProvider';
import { SnowflakeDataProvider } from '../../DataProvider/SnowflakeDataProvider';
import { TableMultiMetricProvider } from '../../DataProvider/TableMultiMetricProvider';
import { DataProviderQueryStatus, DataType } from '../../DataProvider/types';
import {
  usePageConfig,
  useTabContentConfig,
} from '../../PageConfigProvider/PageConfigProvider';
import { RevelioPageId } from '../../PageConfigProvider/types';
import {
  type TableRow,
  useTableSelection,
} from '../../TableSelectionProvider/TableSelectionProvider';
import { TrialUnblurProvider } from '../../TrialUnblurProvider/TrialUnblurProvider';
import { ChartFallback } from '../ChartFallback';
import {
  ChartHeader,
  ChartHeaderTitle,
  ChartHeaderTitleInfoWrapper,
  ChartTitleInfo,
} from '../ChartHeader';
import { useChart } from '../ChartProvider';
import { ChartCard, type ChartProps } from '../ChartRoot/Chart';
import { ChartActionMenuWrapper } from '../chart-menu/ChartActionMenuWrapper';
import { FullPostingInfo } from '../FullPostingInfo/FullPostingInfo';
import { transformRowToPostingData } from '../FullPostingInfo/transformRowToPostingData';
import { processMultiMetricData } from '../utils/process-multi-metric-data';
import { convertTableData } from './convert-table-data';
import { PostingDiscoveryCardView } from './PostingDiscoveryCardView';
import {
  POSTING_DISCOVERY_HIDDEN_COLUMNS,
  POSTING_DISCOVERY_NUMERIC_SORT_KEYS,
  POSTINGS_DISCOVERY_DEFAULT_COLUMNS,
  validatePostingsDiscoveryColumns,
} from './posting-discovery-columns';
import { ReportTableCard } from './ReportTableCard';
import { PopoutContentSelector } from './RowPopoutSelector';
import { ScreenerTableChartContent } from './ScreenerTableChartContent';
import { SelectAllCheckbox } from './SelectAllCheckbox';
import { Table } from './Table';
import styles from './TableChart.module.css';
import { TableChartExportMeta } from './TableChartExportMeta';
import { TableControlsProvider } from './TableControlsContext';
import { TalentDiscoveryCardView } from './TalentDiscoveryCardView';
import {
  TALENT_DISCOVERY_HIDDEN_COLUMNS,
  TALENT_DISCOVERY_NUMERIC_SORT_KEYS,
  validateTalentDiscoveryColumns,
} from './talent-discovery-columns';
import type { ColumnMetaMap } from './utils/convertSeriesToTableData';
import {
  getPrimaryColumnHeader,
  TD_DEFAULT_COLUMNS,
} from './utils/table-helpers';
import { migrateColumnNames } from './utils/talent-discovery-column-mappings';
import { ViewModeToggle } from './ViewModeToggle';

type TableChartProps = Pick<
  ChartProps,
  'item' | 'title' | 'onClick' | 'primaryFilter'
> & {
  data?: TableRow[];
  columnMetaMap?: ColumnMetaMap;
  onSelectRow?: (data: object) => void;
};

const getPrimaryDimension = (
  revelioPageId: RevelioPageId | undefined
): PrimaryFilterId | null => {
  if (revelioPageId === RevelioPageId.COMPANY_SCREENER_V2) {
    return FilterId.COMPANY;
  }
  if (revelioPageId === RevelioPageId.GEOGRAPHY_SCREENER_V2) {
    return FilterId.GEOGRAPHY;
  }
  if (revelioPageId === RevelioPageId.ROLE_SCREENER_V2) {
    return FilterId.ROLE;
  }
  return null;
};

export const TableChart = (props: TableChartProps) => {
  const { revelioPageId } = useTabContentConfig();

  const isScreenerPage = isScreenerV2Page(revelioPageId);
  const screenerPrimaryDimension = isScreenerPage
    ? getPrimaryDimension(revelioPageId)
    : null;

  if (isScreenerPage) {
    return (
      <TableControlsProvider>
        <ScreenerTableMultiMetricProvider
          primaryDimension={screenerPrimaryDimension ?? undefined}
        >
          {({ data, status }) => (
            <ChartFallback status={status} onClick={props.onClick}>
              <ScreenerTableChartContent {...props} data={data} />
            </ChartFallback>
          )}
        </ScreenerTableMultiMetricProvider>
      </TableControlsProvider>
    );
  }

  const isPostingsDiscoveryPage =
    revelioPageId === RevelioPageId.POSTING_DISCOVERY;
  if (isPostingsDiscoveryPage) {
    return (
      <TableControlsProvider>
        <PostingDiscoveryDataProvider>
          {({ data, status }) => {
            if (
              data?.tableData !== undefined &&
              data.tableData.type !== DataType.TABLE_ROWS
            )
              return <ChartFallback status={DataProviderQueryStatus.Error} />;

            return (
              <ChartFallback
                status={status}
                zIndex={10}
                onClick={props.onClick}
              >
                <TableChartContent {...props} data={data?.tableData?.data} />
              </ChartFallback>
            );
          }}
        </PostingDiscoveryDataProvider>
      </TableControlsProvider>
    );
  }

  const dataSource = props.item?.plot?.dataSource;
  const snowflakeConfig = props.item?.plot?.snowflakeConfig;

  if (dataSource === 'snowflake' && snowflakeConfig) {
    return (
      <TableControlsProvider>
        <SnowflakeDataProvider config={snowflakeConfig} chartType="table">
          {({ data, status }) => {
            const rows: TableRow[] =
              data?.type === DataType.TABLE_ROWS
                ? (data.data as TableRow[])
                : [];

            return (
              <ChartFallback
                status={status}
                onClick={props.onClick ?? undefined}
              >
                <TableChartContent
                  {...props}
                  data={rows}
                  columnMetaMap={undefined}
                />
              </ChartFallback>
            );
          }}
        </SnowflakeDataProvider>
      </TableControlsProvider>
    );
  }

  return (
    <TableControlsProvider>
      <TableMultiMetricProvider
        primaryDimension={props.primaryFilter?.id}
        secondaryDimension={props.item?.plot?.secondaryDimension}
      >
        {({ data, status }) => (
          <>
            {/* `table` routes through TableChart (not DataWrappedChart), so
                the global-assistant chart-preview overlay needs the table's
                load status reported here for the overlay to clear. */}
            <GlobalAiChartPreviewDataReporter
              itemId={props.item.id}
              branch="multi"
              status={status}
              error={null}
              data={data}
              chartType="table"
              metric={props.item?.plot?.defaultMetricOptions?.[0]}
            />
            <ChartFallback status={status} onClick={props.onClick}>
              <TableDataWithParameterAggregation {...props} data={data} />
            </ChartFallback>
          </>
        )}
      </TableMultiMetricProvider>
    </TableControlsProvider>
  );
};

type TableDataWithParameterAggregationProps = Omit<TableChartProps, 'data'> & {
  data?: MultiMetricProviderResult;
};
const TableDataWithParameterAggregation = (
  props: TableDataWithParameterAggregationProps
) => {
  const { data: rawData } = props;
  const { item } = useChart();
  const { config: pageConfig } = usePageConfig();
  const { activeFilterMap } = useFilterContext();

  const multiMetricData = useMemo(() => {
    const byMetric = rawData?.byMetric;
    if (!byMetric || Object.keys(byMetric).length === 0) {
      return rawData;
    }
    const reportParameters =
      pageConfig?.tabs?.[0]?.tabContent?.reportParameters ?? [];
    const plotParameters = item?.plot?.plotParameters ?? [];
    const aggregatedByMetric = processMultiMetricData({
      byMetric,
      plotParameters,
      reportParameters,
      activeFilterMap,
    });
    return { byMetric: aggregatedByMetric };
  }, [rawData, item?.plot?.plotParameters, pageConfig?.tabs, activeFilterMap]);

  const effectiveScreenerDimension =
    item?.plot?.dimensionSource && item.plot.dimensionSource !== 'primary'
      ? dimensionSourceToFilterId(item.plot.dimensionSource)
      : undefined;

  const primaryFilter = props.primaryFilter;
  const effectivePrimaryDimension =
    effectiveScreenerDimension ?? primaryFilter?.id;

  const result = convertTableData({
    data: multiMetricData?.byMetric,
    secondaryDimension: item?.plot?.secondaryDimension,
    primaryDimension: effectivePrimaryDimension,
  });

  const data = (() => {
    if (Array.isArray(result)) {
      return result;
    }

    if (!effectiveScreenerDimension) return result.rows;

    const columnName = getPrimaryColumnHeader(effectivePrimaryDimension);
    if (columnName === 'Primary') return result.rows;

    return result.rows.map((row) => {
      if (!('Primary' in row)) return row;
      const { Primary, ...rest } = row;
      return { [columnName]: Primary, ...rest };
    });
  })();

  const columnMetaMap = (() => {
    if (Array.isArray(result)) {
      return undefined;
    }

    return result.columnMetaMap;
  })();

  return (
    <TableChartContent {...props} data={data} columnMetaMap={columnMetaMap} />
  );
};

const TableChartContent = ({
  item,
  title,
  data: tableData = [],
  columnMetaMap,
  primaryFilter,
  onClick,
  onSelectRow,
}: TableChartProps) => {
  const { variant } = useChart();
  const isReport = variant === 'report';
  const { revelioPageId } = useTabContentConfig();
  const isPostingDiscoveryPage =
    revelioPageId === RevelioPageId.POSTING_DISCOVERY;
  const isTalentDiscoveryPage =
    revelioPageId === RevelioPageId.TALENT_DISCOVERY;

  const popoutChartType =
    item?.plot?.tableRowPopout?.chartType ?? ('full-profile-info' as const);

  const getRowDetail = useMemo(() => {
    if (!isTalentDiscoveryPage && !isPostingDiscoveryPage) return undefined;
    if (isPostingDiscoveryPage) {
      return (row: TableRow) => (
        <FullPostingInfo
          data={transformRowToPostingData(row as PostingDiscoveryRow)}
        />
      );
    }
    return (row: TableRow) => (
      <PopoutContentSelector
        chartType={popoutChartType}
        rowData={row as Parameters<typeof PopoutContentSelector>[0]['rowData']}
      />
    );
  }, [isTalentDiscoveryPage, isPostingDiscoveryPage, popoutChartType]);
  const [viewMode, setViewMode] = useState<'list' | 'card'>('list');
  const [sortedTableData, setSortedTableData] = useState<TableRow[]>([]);
  const [sortingState, setSortingState] = useState<SortingState>([]);
  const { setSelectedRows, selectedRows, setAllRows } = useTableSelection();

  const handleViewModeChange = useCallback((mode: 'list' | 'card') => {
    setViewMode(mode);
  }, []);

  const hasActiveSorting = sortingState.length > 0;

  useEffect(() => {
    if (viewMode !== 'card' || hasActiveSorting) {
      return;
    }
    setSortedTableData(tableData);
  }, [hasActiveSorting, tableData, viewMode]);

  const exportData = useMemo(
    () =>
      tableData.length > 0
        ? { type: DataType.TABLE_ROWS, data: tableData }
        : undefined,
    [tableData]
  );

  useEffect(() => {
    setAllRows(tableData);
  }, [tableData, setAllRows]);

  const handleSortedDataChange = useCallback((rows: TableRow[]) => {
    setSortedTableData(rows);
  }, []);

  const cardData = hasActiveSorting ? sortedTableData : tableData;

  const numOfRecords = tableData.length;

  const showNumOfRecords = numOfRecords > 0;

  const tagLabel =
    numOfRecords >= 5000
      ? `More than ${numOfRecords.toLocaleString()}`
      : numOfRecords.toLocaleString();

  const onRowSelectionChange = useCallback(
    (rows: TableRow[]) => {
      setSelectedRows(rows);
    },
    [setSelectedRows]
  );

  // No alternating row colors in themed (Dashboard 2026) appearances — the new
  // table/card design uses a uniform surface. Legacy keeps its zebra striping.
  const { isNewTheme } = useChartTheme();
  const showZebraStripe = isNewTheme ? false : item?.plot?.showZebraStripe;
  const enableRowSelection = item?.plot?.enableRowSelection !== false;

  const cardViewProps = {
    data: cardData,
    enableRowSelection,
    onRowSelectionChange,
    selectedRows,
    showZebraStripe,
    popoutChartType:
      item?.plot?.tableRowPopout?.chartType ?? ('full-profile-info' as const),
  };

  let tableNumericSortKeys: Set<string> | undefined;
  let tableDefaultColumns: string[] | undefined;
  if (isTalentDiscoveryPage) {
    tableNumericSortKeys = TALENT_DISCOVERY_NUMERIC_SORT_KEYS;
    tableDefaultColumns = TD_DEFAULT_COLUMNS;
  }
  if (isPostingDiscoveryPage) {
    tableNumericSortKeys = POSTING_DISCOVERY_NUMERIC_SORT_KEYS;
    tableDefaultColumns = POSTINGS_DISCOVERY_DEFAULT_COLUMNS;
  }

  const filterColumn = (() => {
    if (!isTalentDiscoveryPage && !isPostingDiscoveryPage) return () => true;
    const hidden = isPostingDiscoveryPage
      ? POSTING_DISCOVERY_HIDDEN_COLUMNS
      : TALENT_DISCOVERY_HIDDEN_COLUMNS;
    return (key: string) => !hidden.includes(key);
  })();

  const processResolvedColumns = useMemo(() => {
    if (!isTalentDiscoveryPage && !isPostingDiscoveryPage) return undefined;
    if (isPostingDiscoveryPage) return validatePostingsDiscoveryColumns;
    return (columns: string[]) =>
      validateTalentDiscoveryColumns(migrateColumnNames(columns));
  }, [isTalentDiscoveryPage, isPostingDiscoveryPage]);

  const columnStorageKey = `columns_${revelioPageId ?? 'custom_dash'}`;

  const tableContent =
    viewMode === 'card' ? (
      <Box
        h="100%"
        overflow="hidden"
        className={classNames(styles.cardViewContainer)}
      >
        {isPostingDiscoveryPage ? (
          <PostingDiscoveryCardView
            enableRowSelection={enableRowSelection}
            onRowSelectionChange={onRowSelectionChange}
            selectedRows={selectedRows as PostingDiscoveryRow[]}
            showZebraStripe={showZebraStripe}
            data={tableData as PostingDiscoveryRow[]}
          />
        ) : (
          <TalentDiscoveryCardView {...cardViewProps} />
        )}
      </Box>
    ) : (
      <Table
        data={tableData}
        columnMetaMap={columnMetaMap}
        onSelectRow={onSelectRow}
        enableRowSelection={enableRowSelection}
        onSortedDataChange={
          isTalentDiscoveryPage || isPostingDiscoveryPage
            ? handleSortedDataChange
            : undefined
        }
        onSortingStateNotify={
          isTalentDiscoveryPage || isPostingDiscoveryPage
            ? setSortingState
            : undefined
        }
        filterColumn={filterColumn}
        numericSortKeys={tableNumericSortKeys}
        defaultColumns={tableDefaultColumns}
        getRowDetail={getRowDetail}
        columnStorageKey={columnStorageKey}
        processResolvedColumns={processResolvedColumns}
      />
    );

  const wrappedTableContent = isTalentDiscoveryPage ? (
    <TrialUnblurProvider>{tableContent}</TrialUnblurProvider>
  ) : (
    tableContent
  );

  const tableExportEnabled = Boolean(exportData);

  const exportRegistration = item?.id ? (
    <>
      <ChartActionMenuWrapper
        chartId={item.id}
        title={title || 'Table'}
        primaryFilterId={primaryFilter?.id}
        plotChartType="table"
        data={exportData}
        dataType={exportData ? DataType.TABLE_ROWS : undefined}
      />
      <TableChartExportMeta
        chartId={item.id}
        enabled={tableExportEnabled}
        columnMetaMap={columnMetaMap}
        plotFormat={item?.plot?.format}
      />
    </>
  ) : null;

  if (isReport) {
    return (
      <>
        {exportRegistration}
        <ReportTableCard title={title} onClick={onClick}>
          {wrappedTableContent}
        </ReportTableCard>
      </>
    );
  }

  return (
    <>
      {exportRegistration}
      <ChartCard onClick={onClick} position="relative">
        <ChartHeader
          paddingLeft="0.875rem"
          paddingRight="0.875rem"
          gap="0"
          fontSize="1rem"
          lineHeight="44px"
        >
          <ChartHeaderTitleInfoWrapper
            {...(item.info && {
              chartTitleInfo: <ChartTitleInfo info={item.info} />,
            })}
            width="100%"
            className={styles.headerBorder}
            borderTopRadius="6px"
            alignItems="center"
            justifyContent="space-between"
            px="0.5rem"
          >
            <ChartHeaderTitle>
              {enableRowSelection && tableData.length > 0 && (
                <SelectAllCheckbox
                  tableData={tableData}
                  selectedRows={selectedRows}
                  onRowSelectionChange={onRowSelectionChange}
                />
              )}
              {title}
              {showNumOfRecords && (
                <Tag
                  size="sm"
                  variant="outline"
                  className={styles.recordCountBadge}
                >
                  {tagLabel}
                </Tag>
              )}
            </ChartHeaderTitle>
            {(isTalentDiscoveryPage || isPostingDiscoveryPage) &&
              tableData.length > 0 && (
                <ViewModeToggle
                  viewMode={viewMode}
                  onViewModeChange={handleViewModeChange}
                />
              )}
          </ChartHeaderTitleInfoWrapper>
        </ChartHeader>
        <CardBody
          data-testid="card-body"
          padding="0 14px 0.5rem"
          height="calc(100% - 57px)"
        >
          {wrappedTableContent}
        </CardBody>
      </ChartCard>
    </>
  );
};
